/**
 * Button component styles
 * Uses CSS custom properties from global.css for consistency
 */

/* ==========================================================================
   Base Button Styles
   ========================================================================== */

.button {
  /* Layout */
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-2);
  position: relative;

  /* Typography */
  font-family: var(--font-family-sans);
  font-weight: var(--font-weight-medium);
  line-height: var(--line-height-tight);
  text-decoration: none;
  white-space: nowrap;

  /* Appearance */
  border-radius: var(--radius-md);
  border: 1px solid transparent;
  cursor: pointer;

  /* Transitions */
  transition:
    background-color var(--transition-fast),
    border-color var(--transition-fast),
    color var(--transition-fast),
    box-shadow var(--transition-fast),
    transform var(--transition-fast);

  /* User interaction */
  user-select: none;
  -webkit-tap-highlight-color: transparent;
}

/* Focus state for keyboard navigation */
.button:focus-visible {
  outline: 2px solid var(--color-primary-500);
  outline-offset: 2px;
}

/* Active press effect */
.button:active:not(:disabled) {
  transform: translateY(1px);
}

/* Disabled state */
.button:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

/* ==========================================================================
   Size Variants
   ========================================================================== */

.sm {
  height: var(--button-height-sm);
  padding: 0 var(--button-padding-x-sm);
  font-size: var(--font-size-sm);
  border-radius: var(--radius-base);
}

.md {
  height: var(--button-height);
  padding: 0 var(--button-padding-x);
  font-size: var(--font-size-base);
}

.lg {
  height: var(--button-height-lg);
  padding: 0 var(--button-padding-x-lg);
  font-size: var(--font-size-lg);
  border-radius: var(--radius-lg);
}

/* ==========================================================================
   Color Variants
   ========================================================================== */

/* Primary - Solid background */
.primary {
  background-color: var(--color-primary-600);
  border-color: var(--color-primary-600);
  color: #ffffff;
}

.primary:hover:not(:disabled) {
  background-color: var(--color-primary-700);
  border-color: var(--color-primary-700);
}

.primary:active:not(:disabled) {
  background-color: var(--color-primary-800);
  border-color: var(--color-primary-800);
}

.primary:focus-visible {
  box-shadow: var(--shadow-focus);
}

/* Secondary - Solid secondary color */
.secondary {
  background-color: var(--color-secondary-600);
  border-color: var(--color-secondary-600);
  color: #ffffff;
}

.secondary:hover:not(:disabled) {
  background-color: var(--color-secondary-700);
  border-color: var(--color-secondary-700);
}

.secondary:active:not(:disabled) {
  background-color: var(--color-secondary-800);
  border-color: var(--color-secondary-800);
}

.secondary:focus-visible {
  box-shadow: 0 0 0 3px rgba(20, 184, 166, 0.5);
}

/* Outline - Border only, transparent background */
.outline {
  background-color: transparent;
  border-color: var(--color-neutral-300);
  color: var(--color-neutral-700);
}

.outline:hover:not(:disabled) {
  background-color: var(--color-neutral-50);
  border-color: var(--color-neutral-400);
  color: var(--color-neutral-800);
}

.outline:active:not(:disabled) {
  background-color: var(--color-neutral-100);
}

.outline:focus-visible {
  border-color: var(--color-primary-500);
  box-shadow: var(--shadow-focus);
}

/* ==========================================================================
   Full Width
   ========================================================================== */

.fullWidth {
  width: 100%;
}

/* ==========================================================================
   Loading State
   ========================================================================== */

.loading {
  pointer-events: none;
}

.spinner {
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
}

.spinnerIcon {
  width: 1.25em;
  height: 1.25em;
  animation: spin 1s linear infinite;
}

.spinnerCircle {
  stroke-dasharray: 32;
  stroke-dashoffset: 32;
  stroke-linecap: round;
  animation: dash 1.5s ease-in-out infinite;
}

.content {
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
}

.contentHidden {
  visibility: hidden;
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes dash {
  0% {
    stroke-dashoffset: 32;
  }
  50% {
    stroke-dashoffset: 8;
  }
  100% {
    stroke-dashoffset: 32;
  }
}
